import { useQuery } from '@tanstack/react-query'
import React, { useState, useEffect } from 'react'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import LoadingPage from '../LoadingPage'
import InstructorSurveyListFeature from '../../features/InstructorDashboardFeature'
import _ from 'lodash'
import Box from '@mui/material/Box'
import SkipNavigation from '../../components/SkipNavigation'
import Loader from '../../components/Loader'
import { useParams, useSearchParams } from 'react-router-dom'
import { type InstructorDistribution } from '../../models/Distribution'
import TermHeader from '../../components/TermHeader'
import { Export } from '../../features/InstructorDashboardFeature/components/Export'
import { getUserProfile } from '../../store/slices/userProfileSlice/selectors'
import { DistributionManager, TermsManager, UserManager } from '../../APIManager'

const InstructorDashboardPage: React.FC = (): JSX.Element => {
  const [instructorName, setInstructorName] = useState('')
  const [instructorSurveys, setInstructorSurveys] = useState<InstructorDistribution[]>([])
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const termId = searchParams.get('termId')
  const user = getUserProfile()
  const { userId } = user ?? {}

  const { data: terms = [], isLoading: termsLoading } = useQuery({
    queryKey: ['terms', userId],
    staleTime: 1000,
    queryFn: async () => (await TermsManager.getAllTerms({ pageSize: 200, userId })).data,
    retry: false,
    enabled: !!userId
  })

  const { data: instructorDistributions = [], isLoading } = useQuery({
    queryKey: id ? ['instructorDistributions', id, termId] : ['instructorDistributions', termId],
    queryFn: async () => {
      const params = { termId }
      if (id) {
        return (await DistributionManager.getInstructorDistributionsById(id, params)).data
      }
      return (await DistributionManager.getInstructorDistributions(params)).data
    },
    enabled: !!termId,
    retry: false
  })

  const { data: facultyData, isLoading: facultyLoading } = useQuery({
    queryKey: ['faculty', id, termId],
    queryFn: async () => {
      if (!id) return null
      const params = { termId }
      return (await UserManager.getFacultyAndStaffByIdApi(id, params)).data
    },
    enabled: !!id
  })

  useEffect(() => {
    if (facultyData) {
      setInstructorName(`${facultyData.firstName} ${facultyData.lastName}`)
    }
  }, [facultyData])

  useEffect(() => {
    if (!id && user) {
      setInstructorName(user?.name ?? 'Courses')
    }
  }, [user, id])

  const getSortedDistributions = (surveys: InstructorDistribution[]): InstructorDistribution[] => {
    const currentDate = new Date()

    const upcomingSurveys: InstructorDistribution[] = []
    const ongoingSurveys: InstructorDistribution[] = []
    const completedSurveys: InstructorDistribution[] = []

    surveys.forEach((survey) => {
      const start = new Date(survey.startTimestamp)
      const end = new Date(survey.endTimestamp)

      if (start > currentDate) {
        upcomingSurveys.push(survey)
      } else if (end < currentDate) {
        completedSurveys.push(survey)
      } else {
        ongoingSurveys.push(survey)
      }
    })

    const sortByCourse = ['courseName', 'sectionAbbr']

    const sortedOngoing = _.orderBy(ongoingSurveys, ['endTimestamp', ...sortByCourse], ['asc', 'asc', 'asc'])
    const sortedCompleted = _.orderBy(completedSurveys, ['releaseTimestamp', ...sortByCourse], ['asc', 'asc', 'asc'])
    const sortedUpcoming = _.orderBy(upcomingSurveys, ['startTimestamp', ...sortByCourse], ['asc', 'asc', 'asc'])

    return [...sortedOngoing, ...sortedCompleted, ...sortedUpcoming]
  }

  useEffect(() => {
    if (instructorDistributions?.length) {
      const sortedInstructorDistributions = getSortedDistributions(instructorDistributions)
      setInstructorSurveys(sortedInstructorDistributions)
    } else {
      setInstructorSurveys([])
    }
  }, [instructorDistributions])

  if (isLoading || facultyLoading) {
    return <Loader open={isLoading} />
  }

  return (
    <Box>
      <SkipNavigation />
      <CustomHeader info={isLoading ? PAGE_TITLES.INSTRUCTOR_DASHBOARD : instructorName} />
      {isLoading && <LoadingPage title={PAGE_TITLES.INSTRUCTOR_DASHBOARD} />}
      <InstructorSurveyListFeature
        list={instructorSurveys}
        instructorFullName={instructorName}
        controls={
          <TermHeader showSeries={false} terms={terms} termsLoading={termsLoading}>
            <Export />
          </TermHeader>
        }
      />
    </Box>
  )
}

export default InstructorDashboardPage
